import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Hash,
  Save,
  Loader2,
  FolderKanban,
  Receipt,
  FileText,
  Ticket,
  RefreshCw,
  Info
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const NomenclatureSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    // Projets
    project_prefix: 'PRJ',
    project_format: '{PREFIX}-{YEAR}-{NUM}',
    project_counter: 1,
    
    // Reçus
    receipt_prefix: 'REC',
    receipt_format: '{PREFIX}-{YEAR}-{MONTH}-{NUM}',
    receipt_counter: 1,
    
    // Tickets
    ticket_prefix: 'TKT',
    ticket_format: '{PREFIX}-{NUM}',
    ticket_counter: 1,
    
    // Factures
    invoice_prefix: 'FAC',
    invoice_format: '{PREFIX}-{YEAR}-{NUM}',
    invoice_counter: 1,
    
    // Devis
    quote_prefix: 'DEV',
    quote_format: '{PREFIX}-{YEAR}-{NUM}',
    quote_counter: 1
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings/nomenclature');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Error fetching nomenclature settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/nomenclature', settings);
      toast.success('Nomenclatures enregistrées');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getPreview = (type) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    
    let format = settings[`${type}_format`];
    let prefix = settings[`${type}_prefix`];
    let counter = settings[`${type}_counter`] || 1;
    
    return format
      .replace('{PREFIX}', prefix)
      .replace('{YEAR}', year)
      .replace('{MONTH}', month)
      .replace('{NUM}', String(counter).padStart(4, '0'));
  };

  const nomenclatureItems = [
    { 
      key: 'project', 
      label: 'Projets', 
      icon: FolderKanban, 
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Numérotation des projets créés'
    },
    { 
      key: 'receipt', 
      label: 'Reçus', 
      icon: Receipt, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      description: 'Numérotation des reçus de paiement'
    },
    { 
      key: 'ticket', 
      label: 'Tickets', 
      icon: Ticket, 
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Numérotation des tickets support'
    },
    { 
      key: 'invoice', 
      label: 'Factures', 
      icon: FileText, 
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      description: 'Numérotation des factures émises'
    },
    { 
      key: 'quote', 
      label: 'Devis', 
      icon: FileText, 
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      description: 'Numérotation des devis'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <Hash className="w-8 h-8" />
              Nomenclatures
            </h1>
            <p className="text-[#64748b] mt-1">
              Configurez les préfixes et formats de numérotation
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>

        {/* Info Card */}
        <Card className="card mb-6 border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-800 mb-1">Variables disponibles :</p>
              <code className="text-xs bg-slate-100 px-2 py-1 rounded">{'{PREFIX}'}</code> - Préfixe défini • 
              <code className="text-xs bg-slate-100 px-2 py-1 rounded ml-2">{'{YEAR}'}</code> - Année (2026) • 
              <code className="text-xs bg-slate-100 px-2 py-1 rounded ml-2">{'{MONTH}'}</code> - Mois (01-12) • 
              <code className="text-xs bg-slate-100 px-2 py-1 rounded ml-2">{'{NUM}'}</code> - Numéro séquentiel
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors?.primary }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {nomenclatureItems.map((item) => (
              <Card key={item.key} className="card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className={cn("p-2 rounded-lg", item.bgColor)}>
                      <item.icon className={cn("w-4 h-4", item.color)} />
                    </div>
                    {item.label}
                  </CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Préfixe</Label>
                      <Input 
                        value={settings[`${item.key}_prefix`]}
                        onChange={(e) => updateSetting(`${item.key}_prefix`, e.target.value.toUpperCase())}
                        placeholder="Ex: PRJ"
                        className="font-mono"
                        maxLength={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Prochain numéro</Label>
                      <Input 
                        type="number"
                        value={settings[`${item.key}_counter`]}
                        onChange={(e) => updateSetting(`${item.key}_counter`, parseInt(e.target.value) || 1)}
                        min={1}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Format</Label>
                    <Input 
                      value={settings[`${item.key}_format`]}
                      onChange={(e) => updateSetting(`${item.key}_format`, e.target.value)}
                      placeholder="Ex: {PREFIX}-{YEAR}-{NUM}"
                      className="font-mono text-sm"
                    />
                  </div>
                  
                  {/* Preview */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Aperçu</p>
                    <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                      {getPreview(item.key)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default NomenclatureSettingsPage;
